import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Breadcrumbs,
  Link,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  Divider,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip
} from '@mui/material';
import {
  Home as HomeIcon,
  ArrowBack as ArrowBackIcon,
  Receipt as ReceiptIcon,
  Refresh as RefreshIcon,
  Link as LinkIcon,
  Store as StoreIcon,
  Description as DescriptionIcon,
  Payment as PaymentIcon,
  Add as AddIcon,
  AttachFile as AttachFileIcon,
  OpenInNew as OpenInNewIcon,
  Delete as DeleteIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useAuthenticatedFetch } from '../../../hooks/useAuthenticatedFetch';
import { useUserPermissions } from '../../../hooks/useUserPermissions';
import { API_URL } from '../../../utils/config';
import { SubsidiaryInvoiceModal } from '../../../components/accounting/SubsidiaryInvoiceModal';
import { NotesSection } from '../../../components/accounting/NotesSection';

interface LineItem {
  lineNumber: number;
  lineId?: string;
  detailType?: string;
  amount?: number;
  description?: string;
  item?: { value?: string; name?: string } | null;
  account?: { value?: string; name?: string } | null;
  unitPrice?: number;
  quantity?: number;
  taxCode?: { value?: string; name?: string } | null;
  billableStatus?: string;
  customer?: { value?: string; name?: string } | null;
  createdAt?: string;
  updatedAt?: string;
}

interface LinkedInvoice {
  id: string;
  docNumber?: string;
  customer?: {
    id?: string;
    name?: string;
  };
  transactionDate?: string;
  dueDate?: string;
  totalAmount?: number;
  balance?: number;
  currencyCode?: string;
  currencyName?: string;
  mappingReason?: string;
  mappingCreatedAt?: string;
  mappingCreatedBy?: string;
}

interface Payment {
  id: string;
  transactionDate?: string;
  paymentType?: string;
  totalAmount?: number;
  appliedAmount?: number;
  currencyCode?: string;
  currencyName?: string;
  quickBooksCreateTime?: string;
  createdAt?: string;
}

interface SubsidiaryInvoice {
  id: number;
  invoiceNumber: string;
  totalAmount: number;
  currency: string;
  invoiceDate: string;
  paidDate?: string | null;
  documentUrl?: string | null;
  contentType?: string | null;
  createdAt: string;
  updatedAt: string;
}

interface BillDetails {
  id: string;
  syncToken?: string;
  docNumber?: string;
  transactionDate?: string;
  dueDate?: string;
  vendor?: {
    id?: string;
    name?: string;
    displayName?: string;
    companyName?: string;
    email?: string;
    phone?: string;
    balance?: number;
  };
  financial?: {
    totalAmount?: number;
    homeTotalAmount?: number;
    balance?: number;
    homeBalance?: number;
    currencyCode?: string;
    currencyName?: string;
    exchangeRate?: number;
  };
  references?: {
    apAccount?: { value?: string; name?: string } | null;
    department?: { value?: string; name?: string } | null;
  };
  status?: {
    syncStatus?: string;
    lastSyncAt?: string;
    syncError?: string;
  };
  notes?: {
    privateNote?: string;
  };
  metadata?: {
    quickBooksCreateTime?: string;
    quickBooksLastUpdatedTime?: string;
    platformCreatedAt?: string;
    platformUpdatedAt?: string;
  };
  lineItems?: LineItem[];
  linkedInvoices?: LinkedInvoice[];
  payments?: Payment[];
  subsidiaryInvoices?: SubsidiaryInvoice[];
}

export const BillDetailsPage: React.FC = () => {
  const { billId } = useParams<{ billId: string }>();
  const navigate = useNavigate();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const { hasPermission } = useUserPermissions();

  const [bill, setBill] = useState<BillDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [subsidiaryInvoiceModalOpen, setSubsidiaryInvoiceModalOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deletingInvoiceId, setDeletingInvoiceId] = useState<number | null>(null);
  const [isDeletingInvoice, setIsDeletingInvoice] = useState(false);
  const [billInfoModalOpen, setBillInfoModalOpen] = useState(false);

  const fetchBillDetails = async () => {
    if (!billId) return;

    setLoading(true);
    setError(null);
    try {
      const response = await authenticatedFetch(`${API_URL}/api/accounting/admin/bills/${billId}`);

      if (!response || !response.ok) {
        if (response?.status === 403) {
          throw new Error('Access denied. You do not have permission to view this bill.');
        }
        if (response?.status === 404) {
          throw new Error('Bill not found');
        }
        throw new Error('Failed to fetch bill details');
      }

      const result = await response.json();
      setBill(result.data);
    } catch (err: any) {
      console.error('Error fetching bill details:', err);
      setError(err.message || 'Failed to fetch bill details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBillDetails();
  }, [billId]);

  const formatCurrency = (amount?: number, currency: string = 'USD') => {
    if (amount === undefined || amount === null) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const handleDeleteSubsidiaryInvoice = async () => {
    if (!deletingInvoiceId) return;

    setIsDeletingInvoice(true);
    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/accounting/admin/subsidiary-invoices/${deletingInvoiceId}`,
        {
          method: 'DELETE',
        }
      );

      if (response && response.ok) {
        setDeleteConfirmOpen(false);
        setDeletingInvoiceId(null);
        // Refresh the bill details to update the subsidiary invoices list
        fetchBillDetails();
      } else {
        if (response?.status === 403) {
          alert('Access denied. You do not have permission to delete this subsidiary invoice.');
        } else {
          const errorData = await response?.json();
          alert(errorData?.error || 'Failed to delete subsidiary invoice');
        }
      }
    } catch (error) {
      console.error('Error deleting subsidiary invoice:', error);
      alert('Failed to delete subsidiary invoice');
    } finally {
      setIsDeletingInvoice(false);
    }
  };

  const handleDocumentClick = async (invoiceId: number) => {
    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/accounting/admin/subsidiary-invoices/${invoiceId}/document`
      );

      if (response && response.ok) {
        const result = await response.json();
        if (result.success && result.data?.url) {
          window.open(result.data.url, '_blank');
        } else {
          alert('Failed to get document URL');
        }
      } else {
        if (response?.status === 403) {
          alert('Access denied. You do not have permission to view this document.');
        } else {
          const errorData = await response?.json();
          alert(errorData?.error || 'Failed to get document URL');
        }
      }
    } catch (error) {
      console.error('Error getting document URL:', error);
      alert('Failed to get document URL');
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  if (!bill) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="info">
          No bill data available.
        </Alert>
      </Container>
    );
  }

  const isPaid = bill.financial?.balance === 0;
  const isOverdue = bill.dueDate && new Date(bill.dueDate) < new Date() && !isPaid;
  const hasPayments = bill.payments && bill.payments.length > 0;

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          color="inherit"
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => navigate('/')}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
          Home
        </Link>
        <Link
          color="inherit"
          sx={{ cursor: 'pointer' }}
          onClick={() => navigate('/admin/hub')}
        >
          Admin Hub
        </Link>
        {hasPermission('accounting.admin') && (
          <Link
            color="inherit"
            sx={{ cursor: 'pointer' }}
            onClick={() => navigate('/admin/hub/accounting/bills')}
          >
            Bills
          </Link>
        )}
        <Typography color="text.primary">
          Horwath HTL Bill # {bill.docNumber || bill.id}
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              onClick={() => navigate(-1)}
              sx={{ mr: 2 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <ReceiptIcon sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h4" component="h1">
                Horwath HTL Bill # {bill.docNumber || bill.id}
              </Typography>
              <IconButton
                size="small"
                onClick={() => setBillInfoModalOpen(true)}
                sx={{ color: 'info.main' }}
              >
                <InfoIcon />
              </IconButton>
            </Box>
          </Box>
          <IconButton onClick={fetchBillDetails} title="Refresh">
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Main Bill Document */}
      <Paper sx={{ p: 4, mb: 3 }}>
        {/* Bill Header */}
        <Box sx={{ borderBottom: '3px solid #1976d2', pb: 3, mb: 3 }}>
          <Grid container>
            <Grid item xs={6}>
              <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
                Bill # {bill.docNumber || bill.id}
              </Typography>
            </Grid>
            <Grid item xs={6} sx={{ textAlign: 'right' }}>
              <Box sx={{ mb: 2 }}>
                {isPaid ? (
                  <Chip label="PAID" color="success" size="medium" sx={{ fontSize: '1.1rem', px: 2, py: 0.5 }} />
                ) : (
                  <Chip label="OPEN" color="warning" size="medium" sx={{ fontSize: '1.1rem', px: 2, py: 0.5 }} />
                )}
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                {formatCurrency(bill.financial?.totalAmount, bill.financial?.currencyCode)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Balance: {formatCurrency(bill.financial?.balance, bill.financial?.currencyCode)}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Vendor and Bill Details */}
        <Grid container spacing={4} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>VENDOR</Typography>
            <Box>
              <Link
                component="button"
                variant="h6"
                onClick={() => navigate(`/admin/hub/accounting/vendors/${bill.vendor?.id}`)}
                sx={{ textAlign: 'left', display: 'block', mb: 0.5 }}
              >
                {bill.vendor?.displayName || bill.vendor?.name}
              </Link>
              {bill.vendor?.companyName && bill.vendor.companyName !== bill.vendor.displayName && (
                <Typography variant="body2">{bill.vendor.companyName}</Typography>
              )}
              {bill.vendor?.email && (
                <Typography variant="body2" color="text.secondary">{bill.vendor.email}</Typography>
              )}
              {bill.vendor?.phone && (
                <Typography variant="body2" color="text.secondary">{bill.vendor.phone}</Typography>
              )}
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>BILL DETAILS</Typography>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Bill Date:</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2">{formatDate(bill.transactionDate)}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">Due Date:</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2">
                  Upon Invoice Payment
                </Typography>
              </Grid>
              {bill.references?.apAccount?.name && (
                <>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">AP Account:</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">{bill.references.apAccount.name}</Typography>
                  </Grid>
                </>
              )}
              {bill.references?.department?.name && (
                <>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">Department:</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">{bill.references.department.name}</Typography>
                  </Grid>
                </>
              )}
            </Grid>
          </Grid>
        </Grid>

        {/* Private Note */}
        {bill.notes?.privateNote && (
          <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>PRIVATE NOTE</Typography>
            <Typography variant="body2">{bill.notes.privateNote}</Typography>
          </Box>
        )}

        {/* Line Items */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>LINE ITEMS</Typography>
          {bill.lineItems && bill.lineItems.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Account/Item</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Qty</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Unit Price</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bill.lineItems.map((item, index) => (
                    <TableRow key={item.lineId || index}>
                      <TableCell>{item.lineNumber}</TableCell>
                      <TableCell>{item.description || '-'}</TableCell>
                      <TableCell>{item.account?.name || item.item?.name || '-'}</TableCell>
                      <TableCell align="right">
                        {item.quantity !== undefined && item.quantity !== null ? item.quantity : '-'}
                      </TableCell>
                      <TableCell align="right">
                        {item.unitPrice !== undefined && item.unitPrice !== null
                          ? formatCurrency(item.unitPrice, bill.financial?.currencyCode)
                          : '-'}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(item.amount, bill.financial?.currencyCode)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No line items found
            </Typography>
          )}
        </Box>

        {/* Financial Summary */}
        <Box sx={{ borderTop: '2px solid', borderColor: 'divider', pt: 2 }}>
          <Grid container>
            <Grid item xs={12} md={6}>
              {bill.financial?.exchangeRate && bill.financial.exchangeRate !== 1 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Exchange Rate: {bill.financial.exchangeRate}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Home Currency: {formatCurrency(bill.financial?.homeTotalAmount)}
                  </Typography>
                </Box>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ textAlign: 'right' }}>
                <Grid container justifyContent="flex-end">
                  <Grid item xs={6}>
                    <Typography variant="body1">Subtotal:</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: 'right' }}>
                    <Typography variant="body1">
                      {formatCurrency(bill.financial?.totalAmount, bill.financial?.currencyCode)}
                    </Typography>
                  </Grid>
                  {bill.payments && bill.payments.length > 0 && (
                    <>
                      <Grid item xs={6}>
                        <Typography variant="body1">Paid:</Typography>
                      </Grid>
                      <Grid item xs={6} sx={{ textAlign: 'right' }}>
                        <Typography variant="body1" color="success.main">
                          -{formatCurrency(
                            bill.payments.reduce((sum, p) => sum + (p.appliedAmount || 0), 0),
                            bill.financial?.currencyCode
                          )}
                        </Typography>
                      </Grid>
                    </>
                  )}
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Balance Due:</Typography>
                  </Grid>
                  <Grid item xs={6} sx={{ textAlign: 'right' }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 'bold',
                        color: bill.financial?.balance === 0 ? 'success.main' :
                               bill.financial?.balance && bill.financial.balance > 0 ? 'error.main' :
                               'text.primary'
                      }}
                    >
                      {formatCurrency(bill.financial?.balance, bill.financial?.currencyCode)}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      <Grid container spacing={3}>
        {/* Corresponding Horwath HTL Invoice and Subsidiary Invoice */}
        {(bill.linkedInvoices && bill.linkedInvoices.length > 0) || (bill.subsidiaryInvoices && bill.subsidiaryInvoices.length > 0) ? (
          <Grid item xs={12}>
            <Grid container spacing={3}>
              {/* Corresponding Horwath HTL Invoice */}
              {bill.linkedInvoices && bill.linkedInvoices.length > 0 && (
                <Grid item xs={12} lg={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <LinkIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6">Corresponding Horwath HTL Invoice</Typography>
                  </Box>
                  <Grid container spacing={2}>
                    {bill.linkedInvoices.map((invoice) => (
                      <Grid item xs={12} key={invoice.id}>
                        <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                            <Box>
                              <Link
                                component="button"
                                variant="h6"
                                onClick={() => navigate(`/admin/hub/accounting/invoices/${invoice.id}`)}
                                sx={{ fontFamily: 'monospace', textAlign: 'left' }}
                              >
                                Horwath HTL Invoice # {invoice.docNumber || invoice.id}
                              </Link>
                              <Typography variant="body2" color="text.secondary">
                                {formatDate(invoice.transactionDate)}
                              </Typography>
                            </Box>
                            <Chip
                              label={invoice.balance === 0 ? 'Paid' : 'Open'}
                              size="small"
                              color={invoice.balance === 0 ? 'success' : 'warning'}
                            />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body2" gutterBottom>
                              <strong>Customer:</strong>
                            </Typography>
                            <Link
                              component="button"
                              variant="body2"
                              onClick={() => navigate(`/admin/hub/accounting/customers/${invoice.customer?.id}`)}
                              sx={{ display: 'block', mb: 1 }}
                            >
                              {invoice.customer?.name}
                            </Link>
                            <Grid container spacing={1} sx={{ mt: 1 }}>
                              <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary">Amount</Typography>
                                <Typography variant="body2">
                                  {formatCurrency(invoice.totalAmount, invoice.currencyCode)}
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary">Balance</Typography>
                                <Typography variant="body2" sx={{
                                  color: invoice.balance === 0 ? 'success.main' : 'error.main',
                                  fontWeight: 'bold'
                                }}>
                                  {formatCurrency(invoice.balance, invoice.currencyCode)}
                                </Typography>
                              </Grid>
                            </Grid>
                          </Box>
                          <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                            <Typography variant="caption" color="text.secondary">
                              Due: {formatDate(invoice.dueDate)}
                            </Typography>
                          </Box>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              )}

              {/* Subsidiary Invoice */}
              <Grid item xs={12} lg={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <AttachFileIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6">Subsidiary Invoice</Typography>
                  </Box>
                  {(!bill.subsidiaryInvoices || bill.subsidiaryInvoices.length === 0) && (
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => setSubsidiaryInvoiceModalOpen(true)}
                    >
                      Subsidiary Invoice
                    </Button>
                  )}
                </Box>
                {bill.subsidiaryInvoices && bill.subsidiaryInvoices.length > 0 ? (
                  <Grid container spacing={2}>
                    {bill.subsidiaryInvoices.map((invoice) => (
                      <Grid item xs={12} key={invoice.id}>
                        <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                            <Box>
                              <Typography variant="h6" sx={{ fontFamily: 'monospace' }}>
                                Subsidiary Invoice # {invoice.invoiceNumber}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {formatDate(invoice.invoiceDate)}
                              </Typography>
                            </Box>
                            <Tooltip title="Delete Subsidiary Invoice">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setDeletingInvoiceId(invoice.id);
                                  setDeleteConfirmOpen(true);
                                }}
                                color="error"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Grid container spacing={1}>
                              <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary">Amount</Typography>
                                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                  {formatCurrency(invoice.totalAmount, invoice.currency)}
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary">Currency</Typography>
                                <Typography variant="body1">
                                  {invoice.currency}
                                </Typography>
                              </Grid>
                            </Grid>
                            {invoice.paidDate && (
                              <Box sx={{ mt: 2 }}>
                                <Chip
                                  label="Paid"
                                  size="small"
                                  color="success"
                                  sx={{ mr: 1 }}
                                />
                                <Typography variant="caption" color="text.secondary">
                                  {formatDate(invoice.paidDate)}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                          <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="caption" color="text.secondary">
                              Created: {formatDate(invoice.createdAt)}
                            </Typography>
                            {invoice.documentUrl ? (
                              <Button
                                size="small"
                                startIcon={<OpenInNewIcon />}
                                onClick={() => handleDocumentClick(invoice.id)}
                              >
                                View Document
                              </Button>
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                No document
                              </Typography>
                            )}
                          </Box>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Paper sx={{ p: 3 }}>
                    <Typography variant="body2" color="text.secondary" align="center">
                      No subsidiary invoices or documents found. Click the button above to add one.
                    </Typography>
                  </Paper>
                )}
              </Grid>
            </Grid>
          </Grid>
        ) : null}

        {/* Payments */}
        {bill.payments && bill.payments.length > 0 && (
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <PaymentIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">Payments</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                ({bill.payments.length})
              </Typography>
            </Box>
            <Grid container spacing={2}>
              {bill.payments.map((payment) => (
                <Grid item xs={12} md={6} lg={4} key={payment.id}>
                  <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                      <Box>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                          #{payment.id}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(payment.transactionDate)}
                        </Typography>
                      </Box>
                      <Chip
                        label={payment.paymentType || 'Payment'}
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">Total Amount</Typography>
                          <Typography variant="body2">
                            {formatCurrency(payment.totalAmount, payment.currencyCode)}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">Applied</Typography>
                          <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                            {formatCurrency(payment.appliedAmount, payment.currencyCode)}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                    <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                      <Typography variant="caption" color="text.secondary">
                        Currency: {payment.currencyCode || 'USD'}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Grid>
        )}
      </Grid>

      {/* Notes Section */}
      {billId && (
        <Paper sx={{ p: 3, mt: 3 }}>
          <NotesSection
            entityType="bill"
            entityId={billId}
          />
        </Paper>
      )}

      {/* Subsidiary Invoice Modal */}
      {billId && (
        <SubsidiaryInvoiceModal
          open={subsidiaryInvoiceModalOpen}
          onClose={() => setSubsidiaryInvoiceModalOpen(false)}
          billId={billId!}
          onSuccess={() => {
            // Optionally refresh the bill details to show updated information
            fetchBillDetails();
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => !isDeletingInvoice && setDeleteConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this subsidiary invoice? This will also remove the associated document from storage. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteConfirmOpen(false)}
            disabled={isDeletingInvoice}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteSubsidiaryInvoice}
            color="error"
            variant="contained"
            disabled={isDeletingInvoice}
            startIcon={isDeletingInvoice ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {isDeletingInvoice ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bill Information Modal */}
      <Dialog
        open={billInfoModalOpen}
        onClose={() => setBillInfoModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>About Internal Bills</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mt: 1 }}>
            This internal bill serves as a payment placeholder for the corresponding Horwath HTL invoice. Horwath will pay this bill once the invoice payment has been received. The bill amount should match the subsidiary invoice which can be found/viewed below.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBillInfoModalOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};